<template>
  <div
    class="setup-location-page page-table-layout"
    data-testid="setup-location"
  >
    <v-card
      data-testid="setup-location__main-card"
      class="all-width flex-card shadow-off"
      :loading="locationLoading"
      :disabled="locationLoading && !initialLoading"
      loader-height="2"
    >
      <v-card-text class="page-table-layout__body">
        <div v-if="initialLoading" data-testid="setup-location__skeleton-loader">
          <v-row class="mb-4">
            <v-col cols="6">
              <v-skeleton-loader type="heading" class="mb-2" />
              <v-skeleton-loader type="text" />
            </v-col>
            <v-col cols="6" class="d-flex justify-end">
              <v-skeleton-loader type="button" width="140" />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <v-skeleton-loader type="table-heading, table-row-divider@8" />
            </v-col>
          </v-row>
        </div>

        <template v-else>
          <LocationPageHeader
            :title="locationHeaderLabel"
            :loading="locationLoading"
            :can-add="canAddLocation"
            :can-edit-title="canEditLocation"
            :title-saving="titleSaving"
            :show-add-button="!isTrueEmpty"
            :show-toolbar-actions="!isTrueEmpty"
            :has-active-table-filter="showHeaderClearFilter"
            :show-erp-sync="showErpSync"
            :last-sync-label="lastSyncLabel"
            :headers="tableHeaders"
            :reference-name-map="locationNameMap"
            @add-location="handleAddLocation"
            @sync-erp="handleSyncErp"
            @apply-columns="handleApplyColumns"
            @export="handleExport"
            @refresh="handleRefresh"
            @clear-filter="handleClearFilters"
            @update-title="handleUpdateTitle"
          />

          <LocationEmptyState
            v-if="isTrueEmpty"
            variant="no-locations"
            :location-header-label="locationHeaderLabel"
            :can-add="canAddLocation"
            :action-disabled="locationLoading"
            @action="handleAddLocation"
          />

          <LocationEmptyState
            v-else-if="isFilterEmpty"
            variant="filters"
            :location-header-label="locationHeaderLabel"
            :action-disabled="locationLoading"
            @action="handleClearFilters"
          />

          <template v-else>
            <div class="flex-fill-table">
              <LocationTable
                ref="locationTable"
                :rows="paginatedRows"
                :table-data="filteredRows"
                :headers="tableHeaders"
                :items-per-page="itemsPerPage"
                :loading="locationLoading"
                :can-edit="canEditLocation"
                :location-header-label="locationHeaderLabel"
                :page-details="pageDetails"
                @edit="handleEditLocation"
                @toggle-status="handleToggleStatus"
                @filter-change="handleFilterChange"
                @filter-active-change="onFilterActiveChange"
              />
            </div>
          </template>

          <LocationFormDialog
            :value="locationDialog.open"
            :mode="locationDialog.mode"
            :form="form"
            :errors="formErrors"
            :loading="formLoading"
            :country-list="countryList"
            :location-types="locationTypes"
            :location-header-label="locationHeaderLabel"
            @input="onDialogInput"
            @update:form="form = $event"
            @save="handleSaveLocation"
            @cancel="handleCancelDialog"
          />
        </template>
      </v-card-text>
    </v-card>

    <Pagination
      v-if="!initialLoading && filteredRows.length"
      :key="paginationKey"
      :list="filteredRows"
      :itemsperpage="itemsPerPage"
      data-testid="setup-location__pagination"
      @paginated-data="onPaginatedData"
    />
  </div>
</template>

<script>
import './Location.scss';
import { mapActions, mapState } from 'vuex';
import {
  LocationPageHeader,
  LocationTable,
  LocationFormDialog,
  LocationEmptyState,
} from './components';
import { Pagination } from '@/modules/financialclose/components';
import {
  ITEMS_PER_PAGE,
  PAGE_DETAILS,
  RIGHT,
  TABLE_HEADERS,
} from './utils/locationConstants';
import {
  buildLocationFormFromRow,
  clearPersistedTableFilters,
  downloadLocationExcel,
  formatLocationRows,
  validateLocationForm,
} from './utils/locationHelpers';
import { buildLocationPayload } from './utils/locationPayload';

const EMPTY_FORM = () => ({
  _id: null,
  name: '',
  description: '',
  country: '',
  type: '',
  newtype: '',
  setThreshold: false,
  threshold: null,
  thresholdId: null,
});

export default {
  name: 'SetupLocation',

  components: {
    LocationPageHeader,
    LocationTable,
    LocationFormDialog,
    LocationEmptyState,
    Pagination,
  },

  data() {
    return {
      tableHeaders: TABLE_HEADERS.map((header) => ({ ...header })),
      itemsPerPage: ITEMS_PER_PAGE,
      right: RIGHT,
      pageDetails: { ...PAGE_DETAILS },
      formattedRows: [],
      filteredRows: [],
      paginatedRows: [],
      activeColumnFilters: {},
      hasActiveTableFilter: false,
      form: EMPTY_FORM(),
      formErrors: {},
      formLoading: false,
      initialLoading: true,
      paginationKey: 0,
      currentPage: 1,
      lastSyncLabel: '',
      titleSaving: false,
    };
  },

  computed: {
    ...mapState('setup', {
      locationRows: (state) => state.locationRows || [],
      locationLoading: (state) => !!state.locationLoading,
      locationDialog: (state) => state.locationDialog || { open: false, mode: 'add', locationId: null },
      formMeta: (state) => state.formMeta || {},
    }),

    locationHeaderLabel() {
      return this.$store.state.LocationHeader || 'Location';
    },

    locationNameMap() {
      return (this.locationRows || []).reduce((acc, row) => {
        if (row?._id) {
          acc[row._id.toString()] = row.name || '';
        }
        return acc;
      }, {});
    },

    canAddLocation() {
      return this.$nova?.hasRight(this.right, 'add');
    },

    canEditLocation() {
      return this.$nova?.hasRight(this.right, 'edit') || this.canAddLocation;
    },

    showErpSync() {
      return !!this.$store.state.erpEnabled?.locationErpEnabled;
    },

    countryList() {
      const list = this.formMeta?.countryList || [];
      return list.map((item) => (typeof item === 'string' ? { name: item, value: item } : item));
    },

    locationTypes() {
      return this.formMeta?.locationTypes || [];
    },

    thresholds() {
      return this.formMeta?.thresholds || [];
    },

    isTrueEmpty() {
      return !this.initialLoading && this.formattedRows.length === 0;
    },

    isFilterEmpty() {
      return (
        !this.isTrueEmpty &&
        this.filteredRows.length === 0 &&
        (this.hasActiveTableFilter || Object.keys(this.activeColumnFilters || {}).length > 0)
      );
    },

    /** Header clear is only for a filtered table that still has rows. */
    showHeaderClearFilter() {
      return this.hasActiveTableFilter && !this.isFilterEmpty;
    },
  },

  watch: {
    locationRows: {
      deep: true,
      handler() {
        this.syncFormattedRows();
      },
    },

    thresholds: {
      deep: true,
      handler() {
        this.syncFormattedRows();
      },
    },

    locationDialog: {
      deep: true,
      handler(dialog) {
        if (dialog?.open) {
          this.hydrateFormFromDialog(dialog);
        }
      },
    },
  },

  created() {
    this.init();
  },

  methods: {
    ...mapActions('setup', [
      'fetchLocations',
      'createLocation',
      'updateLocation',
      'enableLocation',
      'disableLocation',
      'openLocationDialog',
      'closeLocationDialog',
    ]),

    async init() {
      await this.handleRefresh();
    },

    async handleRefresh() {
      try {
        await this.fetchLocations();
      } catch (err) {
        console.error('[SetupLocation]: fetchLocations error', err);
        this.$store.commit('sbError', err?.message || `Failed to load ${this.locationHeaderLabel}.`);
      } finally {
        this.initialLoading = false;
        this.syncFormattedRows();
      }
    },

    async handleUpdateTitle(nextTitle) {
      this.titleSaving = true;
      try {
        await this.$store.dispatch('updateLocationHeader', nextTitle);
      } catch (err) {
        console.error('[SetupLocation]: updateLocationHeader error', err);
      } finally {
        this.titleSaving = false;
      }
    },

    syncFormattedRows() {
      const locations = Array.isArray(this.locationRows) ? this.locationRows : [];
      this.formattedRows = formatLocationRows(locations, this.thresholds);
      this.applyFilters({ resetPage: false });
    },

    applyFilters({ resetPage = false } = {}) {
      const hasFilters = Object.keys(this.activeColumnFilters || {}).length > 0;
      if (hasFilters && this.$nova?.filterByDynamicCriteria) {
        this.filteredRows = this.$nova.filterByDynamicCriteria(
          this.formattedRows,
          this.activeColumnFilters
        );
      } else {
        this.filteredRows = this.formattedRows.slice();
      }

      const maxPage = Math.ceil(this.filteredRows.length / this.itemsPerPage) || 1;
      if (resetPage || this.currentPage > maxPage || this.currentPage < 1) {
        this.currentPage = 1;
        this.paginationKey += 1;
      }

      const start = (this.currentPage - 1) * this.itemsPerPage;
      this.paginatedRows = this.filteredRows.slice(start, start + this.itemsPerPage);
    },

    onPaginatedData(paginationInfo) {
      if (paginationInfo && typeof paginationInfo === 'object' && 'data' in paginationInfo) {
        this.paginatedRows = Array.isArray(paginationInfo.data) ? paginationInfo.data : [];
        this.currentPage = paginationInfo.page || 1;
      } else if (Array.isArray(paginationInfo)) {
        this.paginatedRows = paginationInfo;
      }
    },

    handleFilterChange(filters) {
      this.activeColumnFilters = filters || {};
      this.applyFilters({ resetPage: true });
    },

    onFilterActiveChange(isActive) {
      this.hasActiveTableFilter = !!isActive;
    },

    handleClearFilters() {
      this.activeColumnFilters = {};
      this.hasActiveTableFilter = false;
      // Table is unmounted in the filter-empty state, so clear Vuex directly.
      // Otherwise the remounted datatable reloads the saved filter.
      clearPersistedTableFilters(this.$store, this.pageDetails);
      this.$refs.locationTable?.clearTableFilter?.();
      this.applyFilters({ resetPage: true });
    },

    handleApplyColumns(headers) {
      this.tableHeaders = headers;
    },

    handleAddLocation() {
      this.form = EMPTY_FORM();
      this.formErrors = {};
      this.openLocationDialog({ mode: 'add', locationId: null });
    },

    handleEditLocation(row) {
      this.form = buildLocationFormFromRow(row);
      this.formErrors = {};
      this.openLocationDialog({ mode: 'edit', locationId: row._id });
    },

    hydrateFormFromDialog(dialog) {
      if (dialog.mode === 'edit' && dialog.locationId) {
        const row = this.formattedRows.find((item) => item._id === dialog.locationId);
        if (row) {
          this.form = buildLocationFormFromRow(row);
        }
      } else if (dialog.mode === 'add') {
        this.form = EMPTY_FORM();
      }
      this.formErrors = {};
    },

    onDialogInput(open) {
      if (!open) {
        this.handleCancelDialog();
      }
    },

    handleCancelDialog() {
      this.closeLocationDialog();
      this.form = EMPTY_FORM();
      this.formErrors = {};
      this.formLoading = false;
    },

    async handleSaveLocation() {
      const errors = validateLocationForm(this.form);
      this.formErrors = errors;
      if (Object.keys(errors).length > 0) {
        this.$store.commit('sbError', 'Please fill all required fields correctly.');
        return;
      }

      const payload = buildLocationPayload({
        ...this.form,
        _id: this.locationDialog.locationId || this.form._id,
      });

      this.formLoading = true;
      try {
        if (this.locationDialog.mode === 'edit') {
          await this.updateLocation(payload);
        } else {
          await this.createLocation(payload);
        }
        this.handleCancelDialog();
        await this.handleRefresh();
      } catch (err) {
        const apiErrors = err?.response?.data?.data?.[0]?.index0;
        if (apiErrors) {
          this.formErrors = apiErrors;
        }
        this.$store.commit(
          'sbError',
          err?.response?.data?.message || err?.message || `Failed to save ${this.locationHeaderLabel}.`
        );
        console.error('[SetupLocation]: handleSaveLocation error', err);
      } finally {
        this.formLoading = false;
      }
    },

    async handleToggleStatus({ item, enabled }) {
      if (!item?._id) return;

      try {
        if (enabled) {
          await this.enableLocation(item._id);
        } else {
          await this.disableLocation(item._id);
        }
        await this.handleRefresh();
      } catch (err) {
        this.$store.commit(
          'sbError',
          err?.response?.data?.message || err?.message || `Failed to update ${this.locationHeaderLabel} status.`
        );
        console.error('[SetupLocation]: handleToggleStatus error', err);
      }
    },

    handleSyncErp() {
      this.$emit('sync-erp');
    },

    handleExport() {
      if (!this.filteredRows.length) {
        this.$store.commit(
          'sbInfo',
          `No ${this.locationHeaderLabel} records to export.`
        );
        return;
      }

      try {
        downloadLocationExcel({
          rows: this.filteredRows,
          headers: this.tableHeaders,
          filename: this.locationHeaderLabel,
        });
      } catch (err) {
        console.error('[SetupLocation]: handleExport error', err);
        this.$store.commit(
          'sbError',
          err?.message || `Failed to export ${this.locationHeaderLabel}.`
        );
      }
    },
  },
};
</script>
